import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, X } from 'lucide-react';
import { toast } from '../../hooks/useToast';
import { getErrorMessage } from '../../lib/api';
import { opportunityService } from '../../services/opportunity.service';
import type { Opportunity, OpportunityStage } from '../../types/crm';
import Loading from '../Loading';

interface OperatorOpportunityListProps {
  operatorId: number;
}

const STAGES: OpportunityStage[] = ['planning', 'in_process', 'won', 'lost'];

const blankForm = () => ({
  title: '',
  stage: 'planning' as OpportunityStage,
  annual_cases: '',
  expected_close_date: '',
});

const titleize = (value: string): string =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const stageBadge = (stage: OpportunityStage): string => {
  switch (stage) {
    case 'won':
      return 'bg-green-100 text-green-800';
    case 'in_process':
      return 'bg-blue-100 text-blue-800';
    case 'lost':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-yellow-100 text-yellow-800';
  }
};

export const OperatorOpportunityList = ({ operatorId }: OperatorOpportunityListProps) => {
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState(blankForm());

  const refresh = () => {
    setIsLoading(true);
    return opportunityService
      .getAll({ 'q[operator_id_eq]': operatorId, per_page: 100 })
      .then((res) => setOpportunities(res.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    opportunityService
      .getAll({ 'q[operator_id_eq]': operatorId, per_page: 100 })
      .then((res) => {
        if (!cancelled) setOpportunities(res.data);
      })
      .catch((err) => {
        if (!cancelled) toast.error(getErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [operatorId]);

  const onCreate = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setIsCreating(true);
    try {
      const created = await opportunityService.create({
        operator_id: operatorId,
        title: form.title.trim(),
        stage: form.stage,
        annual_cases: form.annual_cases || null,
        expected_close_date: form.expected_close_date || null,
      });
      toast.success('Opportunity created');
      setShowAddModal(false);
      setForm(blankForm());
      // Refresh the embedded list so the new row shows up immediately.
      await refresh();
      // Most users want to immediately drill into the new opportunity to add
      // line items + activities, so route them there. Comment out if it
      // turns out the typical flow is to create several in a row instead.
      navigate(`/crm/opportunities/${created.id}`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Opportunities</h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setForm(blankForm());
              setShowAddModal(true);
            }}
            className="btn btn-primary btn-sm inline-flex items-center gap-1"
          >
            <Plus className="h-4 w-4" />
            Add opportunity
          </button>
          <Link to="/crm/opportunities" className="btn btn-secondary btn-sm">
            Open opportunities page
          </Link>
        </div>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-(--color-bg-scrim)" onClick={() => setShowAddModal(false)} />
            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Add opportunity</h3>
                  <button type="button" onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-500">
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="inline_opp_title" className="label">Title</label>
                    <input
                      id="inline_opp_title"
                      type="text"
                      className="input"
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      autoFocus
                    />
                  </div>
                  <div>
                    <label htmlFor="inline_opp_stage" className="label">Stage</label>
                    <select
                      id="inline_opp_stage"
                      className="input"
                      value={form.stage}
                      onChange={(e) => setForm({ ...form, stage: e.target.value as OpportunityStage })}
                    >
                      {STAGES.map((s) => (
                        <option key={s} value={s}>{titleize(s)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="inline_opp_cases" className="label">Annual cases</label>
                      <input
                        id="inline_opp_cases"
                        type="text"
                        inputMode="decimal"
                        className="input"
                        value={form.annual_cases}
                        onChange={(e) => setForm({ ...form, annual_cases: e.target.value })}
                      />
                    </div>
                    <div>
                      <label htmlFor="inline_opp_close" className="label">Expected close</label>
                      <input
                        id="inline_opp_close"
                        type="date"
                        className="input"
                        value={form.expected_close_date}
                        onChange={(e) => setForm({ ...form, expected_close_date: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                <button type="button" onClick={onCreate} disabled={isCreating} className="btn btn-primary">
                  {isCreating ? 'Creating...' : 'Create opportunity'}
                </button>
                <button type="button" onClick={() => setShowAddModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <Loading />
      ) : opportunities.length === 0 ? (
        <div className="text-center text-gray-500 py-8">No opportunities for this operator.</div>
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-4 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                <th className="px-4 py-2 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase">Annual cases</th>
                <th className="px-4 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">Expected close</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {opportunities.map((opp) => (
                <tr key={opp.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600 hover:text-blue-800">
                    <Link to={`/crm/opportunities/${opp.id}`}>{opp.title}</Link>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${stageBadge(opp.stage)}`}>
                      {titleize(opp.stage)}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right">{opp.annual_cases || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{opp.expected_close_date || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OperatorOpportunityList;
